import { useEffect, useState } from 'react';
import { UnitSamples, getUnitSamples } from '../../../services/adminUnits';
import { failureReason } from '../adminErrors';
import UsageChart from './UsageChart';
import { plural } from './unitsFormat';

interface UnitUsageGraphProps {
  unitId: string;
  cpuCores: number;
  memoryGb: number;
  /** A change asks for the samples again, as the live units' readings do. */
  refreshKey?: string;
}

const cores = (value: number) => `${value.toFixed(value < 10 ? 2 : 1)} cores`;
const gb = (value: number) => `${value.toFixed(1)} GB`;

/** A unit's CPU, memory and free disk over the samples still kept. */
export default function UnitUsageGraph({ unitId, cpuCores, memoryGb, refreshKey }: UnitUsageGraphProps) {
  const [samples, setSamples] = useState<UnitSamples | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    getUnitSamples(unitId)
      .then((read) => {
        if (!current) return;
        setSamples(read);
        setError(null);
      })
      .catch((err) => current && setError(failureReason(err)));
    return () => {
      current = false;
    };
  }, [unitId, refreshKey]);

  if (error) return <div className="field"><div className="err">Could not load the samples: {error}</div></div>;
  if (!samples) return <div className="muted" style={{ fontSize: 12.5 }}>Loading…</div>;
  if (samples.points.length === 0) return <div className="muted" style={{ fontSize: 12.5 }}>No samples kept for this unit.</div>;

  const at = (point: { at: string }) => Date.parse(point.at);
  const freeDisk = samples.points.flatMap((point) =>
    point.freeDiskBytes == null ? [] : [{ at: at(point), value: point.freeDiskBytes / 1e9 }]
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: 14 }}>
        <UsageChart title="CPU" points={samples.points.map((point) => ({ at: at(point), value: point.cpuCores }))} format={cores} limit={cpuCores} />
        <UsageChart title="Memory" points={samples.points.map((point) => ({ at: at(point), value: point.memoryBytes / 1e9 }))} format={gb} limit={memoryGb} />
        <UsageChart title="Free disk" points={freeDisk} format={gb} />
      </div>
      <div className="muted" style={{ fontSize: 11.5 }}>
        {plural(samples.samples, 'sample', 'samples')}
        {samples.points.length < samples.samples && `, as ${samples.points.length} points`}
      </div>
    </div>
  );
}
